import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    LinearProgress,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Card,
    CardContent,
    Stack,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Checkbox,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Grid,
    Select,
    MenuItem,
    InputLabel,
    IconButton
} from '@mui/material';
import {
    NavigateNext,
    NavigateBefore,
    Timer,
    CheckCircle,
    Send,
    DragIndicator,
    ArrowUpward,
    ArrowDownward
} from '@mui/icons-material';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    DragOverEvent,
    useDroppable
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { StaticQuizDTO, QuizAttemptDTO, submitQuizAttempt } from '../../services/quizService';
import QuizResults from './QuizResults';
import VoiceQuizRunner, { VoiceQuizAnswer } from '../Abacus/VoiceQuizRunner';

export type QuizAttemptSubmitFn = (data: {
    quizId: number;
    topicId: number;
    answers: Record<string, any>;
    score: number;
    correctAnswers: number;
    timeTakenMinutes: number;
    passed: boolean;
}) => Promise<void>;

interface QuizRunnerProps {
    quiz: StaticQuizDTO;
    mode?: 'PRACTICE' | 'PREVIEW';
    onComplete: (attempt: QuizAttemptDTO) => void;
    onExit: () => void;
    onTryAnother?: () => void;
    hasMoreQuizzes?: boolean;
    /** When provided (e.g. guest flow), used instead of student submitQuizAttempt */
    submitAttempt?: QuizAttemptSubmitFn;
}

// Helper function to shuffle array (Fisher-Yates)
const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
};

// Sortable Item Component
const SortableItem: React.FC<{
    id: string;
    children: React.ReactNode;
    disabled?: boolean;
}> = ({ id, children, disabled }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id, disabled });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: disabled ? 'default' : 'grab'
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {children}
        </div>
    );
};

// Droppable Zone Component
const DroppableZone: React.FC<{
    id: string;
    children: React.ReactNode;
    isOver?: boolean;
}> = ({ id, children, isOver }) => {
    const { setNodeRef } = useDroppable({ id });

    return (
        <div
            ref={setNodeRef}
            style={{
                opacity: isOver ? 0.8 : 1,
                border: isOver ? '3px solid' : '2px dashed',
                borderColor: isOver ? 'primary.main' : 'grey.300'
            }}
        >
            {children}
        </div>
    );
};

const QuizRunner: React.FC<QuizRunnerProps> = ({ 
    quiz, 
    mode = 'PRACTICE', 
    onComplete, 
    onExit,
    onTryAnother,
    hasMoreQuizzes = false,
    submitAttempt: customSubmitAttempt
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, any>>({}); // questionId -> answer
    const [timeLeft, setTimeLeft] = useState((quiz.timeLimitMinutes || 30) * 60);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [attemptResult, setAttemptResult] = useState<QuizAttemptDTO | null>(null);
    
    // Drag and drop state
    const [activeId, setActiveId] = useState<string | null>(null);
    const [draggedItem, setDraggedItem] = useState<string | null>(null);
    
    // Sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Randomize options for each question when displayed to students
    const randomizedQuestions = useMemo(() => {
        return quiz.questions.map(q => {
            const question = { ...q };
            
            // Randomize options for MCQ, MULTIPLE_SELECT, TRUE_FALSE
            if (Array.isArray(question.options) && 
                (question.type === 'MCQ' || question.type === 'MULTIPLE_SELECT' || question.type === 'TRUE_FALSE')) {
                question.options = shuffleArray(question.options);
            }
            
            // Randomize ORDERING question items
            if (question.type === 'ORDERING' && Array.isArray(question.options)) {
                question.options = shuffleArray(question.options);
            }
            
            // Randomize MATCHING right items
            if (question.type === 'MATCHING' && question.options && typeof question.options === 'object') {
                const matchingOpts = question.options as any;
                if (Array.isArray(matchingOpts.rightItems)) {
                    matchingOpts.rightItems = shuffleArray(matchingOpts.rightItems);
                }
            }
            
            // Randomize DRAG_DROP items
            if (question.type === 'DRAG_DROP' && question.options && typeof question.options === 'object') {
                const dragDropOpts = question.options as any;
                if (Array.isArray(dragDropOpts.items)) {
                    dragDropOpts.items = shuffleArray(dragDropOpts.items);
                }
                if (Array.isArray(dragDropOpts.zones)) {
                    dragDropOpts.zones = shuffleArray(dragDropOpts.zones);
                }
            }
            
            return question;
        });
    }, [quiz.questions]);

    // Timer
    useEffect(() => {
        if (mode === 'PREVIEW') return; // No timer in preview

        let timer: NodeJS.Timeout;
        if (timeLeft > 0 && !isSubmitting) {
            timer = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        handleSubmit();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [timeLeft, isSubmitting, mode]);

    const handleAnswer = (value: any) => {
        const question = randomizedQuestions[currentIndex];
        setAnswers(prev => ({
            ...prev,
            [question.id]: value
        }));
    };

    const handleNext = () => {
        if (currentIndex < randomizedQuestions.length - 1) {
            setCurrentIndex(prev => prev + 1);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
        }
    };

    const calculateScore = () => {
        let score = 0;
        randomizedQuestions.forEach(q => {
            const userAnswer = answers[q.id];
            if (isAnswerCorrect(q, userAnswer)) {
                score += q.points || 1;
            }
        });
        return score;
    };

    const isAnswerCorrect = (question: any, userAnswer: any): boolean => {
        if (!userAnswer) return false;

        switch (question.type) {
            case 'MCQ':
            case 'TRUE_FALSE':
            case 'SHORT_ANSWER':
                return String(userAnswer).toLowerCase().trim() === String(question.correctAnswer).toLowerCase().trim();
            
            case 'NUMERIC':
                const correctNum = typeof question.correctAnswer === 'number' ? question.correctAnswer : parseFloat(question.correctAnswer);
                const userNum = typeof userAnswer === 'number' ? userAnswer : parseFloat(userAnswer);
                const tolerance = (Array.isArray(question.options) && question.options[0]?.tolerance) || 0;
                return Math.abs(userNum - correctNum) <= tolerance;
            
            case 'MULTIPLE_SELECT':
                const correctArray = Array.isArray(question.correctAnswer) ? question.correctAnswer.sort() : [];
                const userArray = Array.isArray(userAnswer) ? userAnswer.sort() : [];
                return JSON.stringify(correctArray) === JSON.stringify(userArray);
            
            case 'ORDERING':
                const correctOrder = Array.isArray(question.correctAnswer) ? question.correctAnswer : [];
                const userOrder = Array.isArray(userAnswer) ? userAnswer : [];
                return JSON.stringify(correctOrder) === JSON.stringify(userOrder);
            
            case 'MATCHING':
            case 'DRAG_DROP':
                const correctMapping = typeof question.correctAnswer === 'object' ? question.correctAnswer : {};
                const userMapping = typeof userAnswer === 'object' ? userAnswer : {};
                return JSON.stringify(correctMapping) === JSON.stringify(userMapping);
            
            case 'FILL_BLANK':
                const correctBlanks = typeof question.correctAnswer === 'object' ? question.correctAnswer : {};
                const userBlanks = typeof userAnswer === 'object' ? userAnswer : {};
                return JSON.stringify(correctBlanks) === JSON.stringify(userBlanks);
            
            default:
                return String(userAnswer) === String(question.correctAnswer);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setShowConfirm(false);
        const score = calculateScore();
        const maxScore = randomizedQuestions.reduce((acc, q) => acc + q.points, 0);
        const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
        const passed = percentage >= (quiz.minPassPercent || 70);
        
        // Calculate correct answers
        const correctAnswers = randomizedQuestions.filter(q => {
            const userAnswer = answers[q.id];
            return isAnswerCorrect(q, userAnswer);
        }).length;
        
        // Calculate time taken
        const timeLimitSeconds = (quiz.timeLimitMinutes || 30) * 60;
        const timeTakenMinutes = Math.round((timeLimitSeconds - timeLeft) / 60);

        const attempt: QuizAttemptDTO = {
            quizId: quiz.id!,
            userId: 0, // Injected by service usually
            startedAt: new Date().toISOString(),
            completedAt: new Date().toISOString(),
            score: score, // Store actual score points, not percentage
            answers: answers,
            passed: passed
        };

        // Save attempt (student API or guest via customSubmitAttempt)
        try {
            if (customSubmitAttempt && quiz.id && quiz.topicId) {
                await customSubmitAttempt({
                    quizId: quiz.id,
                    topicId: quiz.topicId,
                    answers,
                    score: percentage,
                    correctAnswers,
                    timeTakenMinutes,
                    passed
                });
            } else if (quiz.id && quiz.topicId) {
                await submitQuizAttempt(
                    quiz.id,
                    quiz.topicId,
                    answers,
                    percentage,
                    correctAnswers,
                    timeTakenMinutes,
                    passed
                );
            }
        } catch (error) {
            console.error('Failed to save quiz attempt:', error);
            // Continue even if save fails - don't block user from seeing results
        }

        setAttemptResult(attempt);
        setShowResults(true);
        onComplete(attempt);
    };

    const handleRetry = () => {
        setShowResults(false);
        setCurrentIndex(0);
        setAnswers({});
        setTimeLeft((quiz.timeLimitMinutes || 30) * 60);
        setAttemptResult(null);
        setShowConfirm(false);
        setActiveId(null);
        setDraggedItem(null);
    };

    // Drag handlers - always provide all three to keep array size consistent
    const [dragOverZone, setDragOverZone] = useState<string | null>(null);
    
    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
        setDraggedItem(event.active.id as string);
    };

    const handleDragOver = (event: DragOverEvent) => {
        const question = randomizedQuestions[currentIndex];
        // Only handle drag over for DRAG_DROP questions
        if (question.type === 'DRAG_DROP') {
            const { over } = event;
            setDragOverZone(over ? (over.id as string) : null);
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveId(null);
        setDraggedItem(null);
        setDragOverZone(null);

        if (!over) return;

        const question = randomizedQuestions[currentIndex];

        if (question.type === 'MATCHING') {
            const matchingOptions = question.options && typeof question.options === 'object' 
                ? (question.options as any) : { leftItems: [], rightItems: [] };
            const leftItems = matchingOptions.leftItems || [];
            const matchingAnswer = typeof answers[question.id] === 'object' ? (answers[question.id] as any) : {};

            const activeItem = active.id as string;
            const overItem = over.id as string;

            // Check if dragging from left to right or vice versa
            if (leftItems.includes(activeItem) && matchingOptions.rightItems?.includes(overItem)) {
                // Matching left item to right item
                const newMapping = { ...matchingAnswer, [activeItem]: overItem };
                handleAnswer(newMapping);
            } else if (matchingOptions.rightItems?.includes(activeItem) && leftItems.includes(overItem)) {
                // Matching right item to left item (reverse)
                const newMapping = { ...matchingAnswer, [overItem]: activeItem };
                handleAnswer(newMapping);
            }
        } else if (question.type === 'DRAG_DROP') {
            const dragDropOptions = question.options && typeof question.options === 'object' 
                ? (question.options as any) : { items: [], zones: [] };
            const dragItems = dragDropOptions.items || [];
            const dropZones = dragDropOptions.zones || [];
            const dragDropAnswer = typeof answers[question.id] === 'object' ? (answers[question.id] as any) : {};

            const activeItem = active.id as string;
            const overZone = over.id as string;

            // Check if dragging item to zone
            if (dragItems.includes(activeItem) && dropZones.includes(overZone)) {
                // Remove item from previous zone if any
                const newMapping: any = {};
                Object.keys(dragDropAnswer).forEach(key => {
                    if (dragDropAnswer[key] !== overZone) {
                        newMapping[key] = dragDropAnswer[key];
                    }
                });
                // Assign item to new zone
                newMapping[activeItem] = overZone;
                handleAnswer(newMapping);
            }
        }
    };

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const isMentalArithmeticOnly = randomizedQuestions.length > 0
        && randomizedQuestions.every((question) => question.type === 'MENTAL_ARITHMETIC');

    const handleMentalComplete = async (voiceAnswers: VoiceQuizAnswer[], score: number) => {
        if (!quiz.id || !quiz.topicId) return;
        const maxScore = randomizedQuestions.reduce((acc, question) => acc + (question.points || 1), 0);
        const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
        const correctAnswers = voiceAnswers.filter((answer) => answer.correct).length;
        const passed = percentage >= (quiz.minPassPercent || 70);

        try {
            if (customSubmitAttempt) {
                await customSubmitAttempt({
                    quizId: quiz.id,
                    topicId: quiz.topicId,
                    answers: voiceAnswers.reduce((acc, answer) => {
                        acc[answer.questionId] = {
                            promptSpoken: answer.promptSpoken,
                            expectedAnswer: answer.expectedAnswer,
                            answerRaw: answer.answerRaw,
                            answerNormalized: answer.answerNormalized,
                            inputMethod: answer.inputMethod,
                            sttConfidence: answer.sttConfidence,
                        };
                        return acc;
                    }, {} as Record<string, any>),
                    score: percentage,
                    correctAnswers,
                    timeTakenMinutes: 0,
                    passed,
                });
            } else {
                await submitQuizAttempt(
                    quiz.id,
                    quiz.topicId,
                    voiceAnswers.reduce((acc, answer) => {
                        acc[answer.questionId] = {
                            promptSpoken: answer.promptSpoken,
                            expectedAnswer: answer.expectedAnswer,
                            answerRaw: answer.answerRaw,
                            answerNormalized: answer.answerNormalized,
                            inputMethod: answer.inputMethod,
                            sttConfidence: answer.sttConfidence,
                        };
                        return acc;
                    }, {} as Record<string, any>),
                    percentage,
                    correctAnswers,
                    0,
                    passed
                );
            }
        } catch (error) {
            console.error('Failed to save voice quiz attempt:', error);
        }

        const attempt: QuizAttemptDTO = {
            quizId: quiz.id,
            userId: 0,
            startedAt: new Date().toISOString(),
            completedAt: new Date().toISOString(),
            score,
            answers: voiceAnswers,
            passed,
        };

        setAttemptResult(attempt);
        setShowResults(true);
        onComplete(attempt);
    };

    // Show results screen if quiz is completed
    if (showResults && attemptResult) {
        return (
            <Paper
                elevation={0}
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.paper',
                    borderRadius: 0,
                    overflow: 'auto'
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: 'grey.50'
                    }}
                >
                    <Typography variant="h6" fontWeight="bold">
                        {quiz.title} - Results
                    </Typography>
                    <Button 
                        color="inherit" 
                        onClick={() => {
                            setShowResults(false);
                            setAttemptResult(null);
                            onExit();
                        }}
                    >
                        Close
                    </Button>
                </Box>
                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <QuizResults
                        quiz={quiz}
                        attempt={attemptResult}
                        onRetry={handleRetry}
                        onTryAnother={onTryAnother}
                        hasMoreQuizzes={hasMoreQuizzes}
                    />
                </Box>
            </Paper>
        );
    }

    if (isMentalArithmeticOnly) {
        return (
            <Paper
                elevation={0}
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.paper',
                    borderRadius: 0
                }}
            >
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" fontWeight="bold">
                        {quiz.title}
                    </Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                    <VoiceQuizRunner
                        questions={randomizedQuestions.map((question) => ({
                            id: question.id,
                            content: question.content,
                            correctAnswer: Number(question.correctAnswer),
                            points: question.points,
                            options: Array.isArray(question.options)
                                ? question.options[0]
                                : (question.options as { tolerance?: number } | undefined),
                        }))}
                        onComplete={handleMentalComplete}
                    />
                    <Box sx={{ mt: 2 }}>
                        <Button color="inherit" onClick={onExit}>Exit</Button>
                    </Box>
                </Box>
            </Paper>
        );
    }

    // --- Render Helpers ---

    const renderOptions = () => {
        const question = randomizedQuestions[currentIndex];
        const questionId = question.id;
        const currentAnswer = answers[questionId];

        switch (question.type) {
            case 'MCQ':
            case 'TRUE_FALSE':
            return (
                <FormControl component="fieldset">
                    <RadioGroup
                            value={currentAnswer || ''}
                        onChange={(e) => handleAnswer(e.target.value)}
                    >
                            {Array.isArray(question.options) && question.options.map((opt: any, idx: number) => (
                            <FormControlLabel
                                key={idx}
                                    value={opt}
                                control={<Radio />}
                                label={opt}
                                sx={{ mb: 1, '& .MuiTypography-root': { fontSize: '1rem' } }}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            );

            case 'SHORT_ANSWER':
                return (
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Type your answer here..."
                        value={currentAnswer || ''}
                        onChange={(e) => handleAnswer(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                );

            case 'NUMERIC':
                return (
                    <TextField
                        fullWidth
                        type="number"
                        variant="outlined"
                        placeholder="Enter a number..."
                        value={currentAnswer || ''}
                        onChange={(e) => handleAnswer(parseFloat(e.target.value) || 0)}
                        sx={{ mt: 2 }}
                        inputProps={{ step: "any" }}
                    />
                );

            case 'MENTAL_ARITHMETIC':
                return (
                    <Typography variant="body2" color="text.secondary">
                        Mental arithmetic mode is handled by the voice quiz runner.
                    </Typography>
                );

            case 'MULTIPLE_SELECT':
                return (
                    <FormControl component="fieldset" sx={{ mt: 2 }}>
                        <Box>
                            {Array.isArray(question.options) && question.options.map((opt: any, idx: number) => {
                                const selected = Array.isArray(currentAnswer) && currentAnswer.includes(opt);
                                return (
                                    <FormControlLabel
                                        key={idx}
                                        control={
                                            <Checkbox
                                                checked={selected}
                                                onChange={(e) => {
                                                    const current = Array.isArray(currentAnswer) ? [...currentAnswer] : [];
                                                    const newAnswer = e.target.checked
                                                        ? [...current, opt]
                                                        : current.filter((a: any) => a !== opt);
                                                    handleAnswer(newAnswer);
                                                }}
                                            />
                                        }
                                        label={opt}
                                        sx={{ mb: 1, '& .MuiTypography-root': { fontSize: '1rem' } }}
                                    />
                                );
                            })}
                        </Box>
                    </FormControl>
                );

            case 'FILL_BLANK':
                const blanks = (question.content || '').split('___');
                const blankAnswer = typeof currentAnswer === 'object' ? (currentAnswer as any).blank1 || '' : '';
                return (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body1" component="span">
                            {blanks[0]}
                            <TextField
                                size="small"
                                value={blankAnswer}
                                onChange={(e) => handleAnswer({ blank1: e.target.value })}
                                sx={{ mx: 1, width: 200 }}
                                placeholder="___"
                            />
                            {blanks[1]}
                        </Typography>
                    </Box>
                );

            case 'ORDERING':
                const orderItems = Array.isArray(question.options) ? question.options : [];
                const currentOrder = Array.isArray(currentAnswer) ? currentAnswer : [...orderItems];
                return (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(event) => {
                            const { active, over } = event;
                            if (over && active.id !== over.id) {
                                const oldIndex = currentOrder.indexOf(active.id as string);
                                const newIndex = currentOrder.indexOf(over.id as string);
                                const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
                                handleAnswer(newOrder);
                            }
                        }}
                    >
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                Drag items to reorder:
                            </Typography>
                            <SortableContext items={currentOrder} strategy={verticalListSortingStrategy}>
                                <List>
                                    {currentOrder.map((item: any, idx: number) => (
                                        <SortableItem key={item} id={item}>
                                            <ListItem sx={{ border: 1, borderColor: 'divider', mb: 1, borderRadius: 1, bgcolor: 'background.paper' }}>
                                                <DragIndicator sx={{ mr: 1, color: 'text.secondary' }} />
                                                <ListItemText primary={item} />
                                            </ListItem>
                                        </SortableItem>
                                    ))}
                                </List>
                            </SortableContext>
                        </Box>
                    </DndContext>
                );

            case 'MATCHING':
                const matchingOptions = question.options && typeof question.options === 'object' 
                    ? (question.options as any) : { leftItems: [], rightItems: [] };
                const leftItems = matchingOptions.leftItems || [];
                const rightItems = matchingOptions.rightItems || [];
                const matchingAnswer = typeof currentAnswer === 'object' ? (currentAnswer as any) : {};
                const availableRightItems = rightItems.filter((right: any) => !Object.values(matchingAnswer).includes(right));
                
                return (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDragEnd={handleDragEnd}
                    >
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                                Drag items from the left to match with items on the right, or drag from right to left.
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">Left Side</Typography>
                                    <SortableContext items={leftItems}>
                                        <Box sx={{ minHeight: 200, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                            {leftItems.map((left: any, idx: number) => {
                                                const matched = matchingAnswer[left];
                                                return (
                                                    <SortableItem key={left} id={left}>
                                                        <Chip
                                                            label={`${left}${matched ? ' → ' + matched : ''}`}
                                                            sx={{
                                                                m: 0.5,
                                                                display: 'inline-block',
                                                                cursor: 'grab',
                                                                bgcolor: matched ? 'success.light' : 'default',
                                                                '&:active': { cursor: 'grabbing' }
                                                            }}
                                                            icon={<DragIndicator />}
                                                        />
                                                    </SortableItem>
                                                );
                                            })}
                                        </Box>
                                    </SortableContext>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">Right Side</Typography>
                                    <SortableContext items={rightItems}>
                                        <Box sx={{ minHeight: 200, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                            {rightItems.map((right: any, rIdx: number) => {
                                                const matchedLeft = Object.keys(matchingAnswer).find(key => matchingAnswer[key] === right);
                                                return (
                                                    <SortableItem key={right} id={right}>
                                                        <Chip
                                                            label={matchedLeft ? `${matchedLeft} → ${right}` : right}
                                                            sx={{
                                                                m: 0.5,
                                                                display: 'inline-block',
                                                                cursor: 'grab',
                                                                bgcolor: matchedLeft ? 'success.light' : 'default',
                                                                '&:active': { cursor: 'grabbing' }
                                                            }}
                                                            icon={<DragIndicator />}
                                                        />
                                                    </SortableItem>
                                                );
                                            })}
                                        </Box>
                                    </SortableContext>
                                </Grid>
                            </Grid>
                        </Box>
                        <DragOverlay>
                            {activeId ? (
                                <Chip
                                    label={activeId}
                                    sx={{ cursor: 'grabbing', opacity: 0.8 }}
                                    icon={<DragIndicator />}
                                />
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                );

            case 'DRAG_DROP':
                const dragDropOptions = question.options && typeof question.options === 'object' 
                    ? (question.options as any) : { items: [], zones: [] };
                const dragItems = dragDropOptions.items || [];
                const dropZones = dragDropOptions.zones || [];
                const dragDropAnswer = typeof currentAnswer === 'object' ? (currentAnswer as any) : {};
                const assignedItems = Object.keys(dragDropAnswer);
                const unassignedItems = dragItems.filter((item: any) => !assignedItems.includes(item));
                
                return (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDragEnd={handleDragEnd}
                    >
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                                Drag items from the pool below into the drop zones.
                            </Typography>
                            
                            {/* Unassigned Items Pool */}
                            <Typography variant="subtitle2" gutterBottom fontWeight="bold">Items:</Typography>
                            <SortableContext items={unassignedItems}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexWrap: 'wrap', 
                                    gap: 1, 
                                    mb: 3, 
                                    p: 2, 
                                    bgcolor: 'grey.100', 
                                    borderRadius: 1,
                                    minHeight: 80
                                }}>
                                    {unassignedItems.map((item: any, idx: number) => (
                                        <SortableItem key={item} id={item}>
                                            <Chip
                                                label={item}
                                                icon={<DragIndicator />}
                                                sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
                                            />
                                        </SortableItem>
                                    ))}
                                    {unassignedItems.length === 0 && (
                                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                            All items have been placed in zones
                                        </Typography>
                                    )}
                                </Box>
                            </SortableContext>
                            
                            {/* Drop Zones */}
                            <Typography variant="subtitle2" gutterBottom fontWeight="bold">Drop Zones:</Typography>
                            <Grid container spacing={2}>
                                {dropZones.map((zone: any, idx: number) => {
                                    const zoneItem = Object.keys(dragDropAnswer).find(key => dragDropAnswer[key] === zone);
                                    return (
                                        <Grid item xs={12} md={6} key={zone}>
                                            <DroppableZone id={zone} isOver={dragOverZone === zone}>
                                                <Box 
                                                    sx={{ 
                                                        mb: 2, 
                                                        p: 3, 
                                                        border: 2, 
                                                        borderStyle: 'dashed', 
                                                        borderRadius: 2,
                                                        borderColor: zoneItem ? 'success.main' : dragOverZone === zone ? 'primary.main' : 'grey.300',
                                                        bgcolor: zoneItem ? 'success.light' : dragOverZone === zone ? 'primary.light' : 'background.paper',
                                                        minHeight: 100,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    {zoneItem ? (
                                                        <Box>
                                                            <Chip
                                                                label={`${zoneItem}`}
                                                                icon={<DragIndicator />}
                                                                sx={{ cursor: 'pointer', mb: 1 }}
                                                                onDoubleClick={() => {
                                                                    // Remove from zone on double click
                                                                    const newMapping: any = {};
                                                                    Object.keys(dragDropAnswer).forEach(key => {
                                                                        if (key !== zoneItem) {
                                                                            newMapping[key] = dragDropAnswer[key];
                                                                        }
                                                                    });
                                                                    handleAnswer(newMapping);
                                                                }}
                                                            />
                                                            <Typography variant="caption" color="text.secondary" display="block" align="center">
                                                                {zone}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary" align="center">
                                                            {zone}
                                                            <br />
                                                            <Typography variant="caption">Drop an item here</Typography>
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </DroppableZone>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                        <DragOverlay>
                            {activeId ? (
                                <Chip
                                    label={activeId}
                                    sx={{ cursor: 'grabbing', opacity: 0.8 }}
                                    icon={<DragIndicator />}
                                />
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                );

            default:
                return <Typography color="error">Unsupported question type: {question.type}</Typography>;
        }
    };

    const currentQuestion = randomizedQuestions[currentIndex];
    const quizQuestionType = randomizedQuestions.length > 0 ? randomizedQuestions[0].type : null;
    const allSameType = randomizedQuestions.every(q => q.type === quizQuestionType);

    return (
        <Paper
            elevation={0}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                borderRadius: 0
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'grey.50'
                }}
            >
                <Box>
                    <Typography variant="h6" fontWeight="bold">
                        {quiz.title}
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" color="text.secondary">
                            Question {currentIndex + 1} of {randomizedQuestions.length}
                    </Typography>
                        {allSameType && quizQuestionType && (
                            <Chip 
                                label={`Type: ${quizQuestionType.replace('_', ' ')}`} 
                                size="small" 
                                variant="outlined"
                            />
                        )}
                    </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={2}>
                    {mode !== 'PREVIEW' && (
                        <Chip
                            icon={<Timer />}
                            label={formatTime(timeLeft)}
                            color={timeLeft < 60 ? 'error' : 'default'}
                            variant="outlined"
                        />
                    )}
                    <Button color="inherit" onClick={onExit}>Exit</Button>
                </Box>
            </Box>

            <LinearProgress
                variant="determinate"
                value={((currentIndex + 1) / randomizedQuestions.length) * 100}
                sx={{ height: 4 }}
            />

            {/* Question Area */}
            <Box sx={{ flexGrow: 1, p: 4, overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Card variant="outlined" sx={{ maxWidth: 900, width: '100%', mb: 4 }}>
                    <CardContent sx={{ p: 4 }}>
                        {!allSameType && (
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                            Question Type: {currentQuestion.type.replace('_', ' ')}
                        </Typography>
                        )}

                        <Box sx={{ my: 3, '& img': { maxWidth: '100%' } }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                                {currentQuestion.content}
                            </ReactMarkdown>
                        </Box>

                        <Box sx={{ mt: 4 }}>
                            {renderOptions()}
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* Footer Navigation */}
            <Box
                sx={{
                    p: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    bgcolor: 'grey.50'
                }}
            >
                <Button
                    startIcon={<NavigateBefore />}
                    onClick={handlePrev}
                    disabled={currentIndex === 0}
                >
                    Previous
                </Button>

                {currentIndex === randomizedQuestions.length - 1 ? (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<CheckCircle />}
                        onClick={() => setShowConfirm(true)}
                    >
                        Submit Quiz
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        endIcon={<NavigateNext />}
                        onClick={handleNext}
                    >
                        Next
                    </Button>
                )}
            </Box>

            {/* Submit Dialog */}
            <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
                <DialogTitle>Submit Quiz?</DialogTitle>
                <DialogContent>
                    <Typography>
                        You are about to submit your answers. You cannot change them after submission.
                        {Object.keys(answers).length < randomizedQuestions.length && (
                            <Typography color="error" sx={{ mt: 1 }}>
                                Warning: You have unanswered questions.
                            </Typography>
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowConfirm(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} autoFocus>
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default QuizRunner;
